<template>
  <div class="w-full">
    <BaseInput
      :id="props.id"
      v-model="value"
      :label="$t('Password')"
      :type="inputType"
      class="mt-4"
    >
      <template #icon>
        <IconWrapper @click="collapsePassword">
          <EyeIcon />
        </IconWrapper>
      </template>
    </BaseInput>

    <div
      v-if="showValidation"
      class="border border-grey-3 mt-3 p-2"
    >
      <div class="text-sm">
        {{ $t('password_must_contain') }}
      </div>
      <div
        v-for="(rule, i) in validationRules"
        :key="i"
        class="flex items-center space-x-4 mt-1"
      >
        <CheckIcon :class="rule.valid ? 'fill-secondary--1' : 'fill-alert-0'" />
        <span
          class="text-sm"
          :class="rule.valid ? 'fill-secondary--1' : 'fill-alert-0'"
        >
          {{ rule.title }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import BaseInput from '@/components/inputs/BaseInput.vue'
import EyeIcon from '@/assets/icons/EyeIcon.vue'
import IconWrapper from '@/components/inputs/ext/IconWrapper.vue'
import { computed, ref, watch } from 'vue'
import CheckIcon from '@/assets/icons/CheckIcon.vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  modelValue: {
    required: true,
    type: String
  },
  valid: {
    type: Boolean,
    default: false
  },
  id: {
    type: String,
    default: 'password',
    required: false
  },
  validationEnabled: {
    type: Boolean,
    default: true
  }
})

const { t } = useI18n()

const emit = defineEmits(['update:modelValue', 'update:valid'])

const value = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const showValidation = computed(() => props.validationEnabled && value.value.length !== 0)

const passwordAsText = ref(false)
const inputType = computed(() => (passwordAsText.value ? 'text' : 'password'))

// array for validation rules
const validationRules = ref([
  {
    regex: '^.{8,}$',
    title: t('password_rule_1'),
    valid: false
  },
  {
    regex: '(?=.*[a-z])',
    title: t('password_rule_2'),
    valid: false
  },
  {
    regex: '(?=.*[A-Z])',
    title: t('password_rule_3'),
    valid: false
  },
  {
    regex: '(?=.*\\d)',
    title: t('password_rule_4'),
    valid: false
  },
  {
    regex: '(?=.*[!@#.$%>&*_/%<{\'}?"])',
    title: t('password_rule_5') + ' (!@#.$%>&*_/%<{\'}?")',
    valid: false
  }
])

const isRuleValid = (rule, password) => new RegExp(rule).test(password)

// when password changes check all rules
watch(
  () => value.value,
  (password) =>
    validationRules.value.forEach((rule) => (rule.valid = isRuleValid(rule.regex, password))),
  { immediate: true }
)

const passwordValid = computed(() => {
  if (!showValidation.value) return true
  return validationRules.value.every((rule) => rule.valid)
})

watch(
  () => passwordValid.value,
  (valid) => emit('update:valid', valid),
  { immediate: true }
)

const showPassword = () => (passwordAsText.value = true)
const hidePassword = () => (passwordAsText.value = false)
const collapsePassword = () => (passwordAsText.value = !passwordAsText.value)
</script>
